import AddIcon from "@mui/icons-material/Add";
import CleaningServicesOutlined from "@mui/icons-material/CleaningServicesOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { PageCard } from "../../components/pageCard/PageCard";
import { PrintPdfDialog } from "../../components/printPdfDialog/PrintPdfDialog";
import { PrintCollectorsPdfDialog } from "../../components/printPdfDialog/PrintCollectorsPdfDialog";
import { CustomMenuItem } from "../../components/table/CustomMenuItem/CustomMenuItem";
import { CustomNoContentTableBody } from "../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../components/table/CustomTablePagination/CustomTablePagination";
import CustomTableSortLabel from "../../components/table/CustomTableSortLabel/CustomTableSortLabel";
import { FilterButtonGroup } from "../../components/table/FilterButtonGroup/FilterButtonGroup";
import { GridFilterArea } from "../../components/table/GridFilterArea/GridFilterArea";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IExhibition } from "../../interfaces/IExhibition";
import { IListAllParams } from "../../interfaces/IListAllParams";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import { downloadBase64Pdf, downloadBase64Zip } from "../../utils/downloadPdf";
import { getUtcOffset } from "../../utils/timezone";

const fields = [
  {
    field: "name",
    label: "Nome",
  },
  {
    field: "details",
    label: "Observação",
  },
];

const STORAGE_KEY = "@ORQUIDEAS:exhibitionsFilters";

const getInitialState = (key: string, defaultValue: any) => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    return parsed[key] !== undefined ? parsed[key] : defaultValue;
  }
  return defaultValue;
};

export const Exhibitions: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();

  const [exhibitions, setExhibitions] = useState<IExhibition[]>([]);
  const [selectedExhibitionId, setSelectedExhibitionId] = useState<
    string | null
  >(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadCollectorsDialogOpen, setDownloadCollectorsDialogOpen] =
    useState(false);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState<number>(() =>
    getInitialState("perPage", 10),
  );
  const [currentPage, setCurrentPage] = useState<number>(() =>
    getInitialState("currentPage", 0),
  );
  const [orderField, setOrderField] = useState<string>(() =>
    getInitialState("orderField", "date"),
  );
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(() =>
    getInitialState("orderDirection", "desc"),
  );
  const [loading, setLoading] = useState(false);

  const [gridFilterAreaOpen, setGridFilterAreaOpen] = useState(() =>
    getInitialState("gridFilterAreaOpen", false),
  );
  const [filterField, setFilterField] = useState<string>(() =>
    getInitialState("filterField", ""),
  );
  const [filterPrecision, setFilterPrecision] = useState<string>(() =>
    getInitialState("filterPrecision", "containing"),
  );
  const [filterValue, setFilterValue] = useState<string>(() =>
    getInitialState("filterValue", ""),
  );
  const [delay, setDelay] = useState(0);

  const handleToUser = useCallback(
    (id: string | null) => {
      !!id ? navigate(`/exhibition/${id}`) : navigate("/exhibition");
    },
    [navigate],
  );

  const handleToFollowUp = useCallback(
    (id: string | null) => {
      if (id) {
        window.open(`/follow-up/${id}`, "_blank");
      } else {
        toastr.error("Exposição não encontrada");
        navigate("/exhibitions");
      }
    },
    [navigate, toastr],
  );

  const handleToResults = useCallback(
    (id: string | null) => {
      if (id) {
        window.open(`/results/${id}`, "_blank");
      } else {
        toastr.error("Exposição não encontrada");
        navigate("/exhibitions");
      }
    },
    [navigate, toastr],
  );

  const handleToBestCollectors = useCallback(
    (id: string | null) => {
      if (id) {
        window.open(`/best-collectors/${id}`, "_blank");
      } else {
        toastr.error("Exposição não encontrada");
        navigate("/exhibitions");
      }
    },
    [navigate, toastr],
  );

  const handleOpenDownloadDialog = useCallback((id: string) => {
    setSelectedExhibitionId(id);
    setDownloadDialogOpen(true);
  }, []);

  const handleOpenDownloadCollectorsDialog = useCallback((id: string) => {
    setSelectedExhibitionId(id);
    setDownloadCollectorsDialogOpen(true);
  }, []);

  const handleDownloadResultsReport = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await api.get(
          `/exhibitions/${id}/results/report?utcOffset=${getUtcOffset()}`,
        );
        const { filename, base64 } = response.data;
        downloadBase64Pdf(base64, filename);
      } catch (error: any) {
        if (error?.response?.status === 422) {
          toastr.warning(
            "Execute a consolidação antes de gerar o relatório. Edite a exposição e marque como Finalizada.",
          );
        } else {
          toastr.error(
            error?.response?.data?.message ||
              "Erro ao gerar relatório de resultados",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [toastr],
  );

  const handleGenerateJudgesCertificates = useCallback(
    async (exhibitionId: string) => {
      if (!exhibitionId) return;
      setLoading(true);
      try {
        const response = await api.post(
          `/exhibitions/${exhibitionId}/evaluation/judges/certificates`,
          { judgeIds: [] },
        );
        const { zip } = response.data as {
          zip: { filename: string; base64: string };
        };
        if (zip) {
          downloadBase64Zip(zip.base64, zip.filename);
          toastr.success("Certificados dos juízes gerados com sucesso!");
        } else {
          toastr.error("Nenhum certificado foi retornado");
        }
      } catch (error: any) {
        toastr.error(
          error?.response?.data?.message ||
            "Erro ao gerar certificados dos juízes",
        );
      } finally {
        setLoading(false);
      }
    },
    [toastr],
  );

  const handleCloseDownloadDialog = useCallback(() => {
    setSelectedExhibitionId(null);
    setDownloadDialogOpen(false);
  }, []);

  const handleCloseDownloadCollectorsDialog = useCallback(() => {
    setSelectedExhibitionId(null);
    setDownloadCollectorsDialogOpen(false);
  }, []);

  const handleDuplicateExhibition = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await api.post(`/exhibitions/${id}/duplicate`);
        toastr.success("Exposição duplicada com sucesso!");
        navigate(`/exhibition/${response.data.id}`);
      } catch (error: any) {
        toastr.error(
          error?.response?.data?.message || "Erro ao duplicar exposição",
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate, toastr],
  );

  const handleListAll = useCallback(
    async ({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    }: IListAllParams) => {
      let url = `exhibitions?perPage=${perPage}&currentPage=${
        currentPage + 1
      }&orderBy=${orderField}&orderDirection=${orderDirection}`;

      if (field && value && precision) {
        url =
          url +
          `&filterField=${field}&filterValue=${value}&precision=${precision}`;
      }

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await api
          .get(url)
          .then(respose => {
            const responseData = respose.data;

            if (responseData.data?.length > 0) {
              setExhibitions(responseData.data);
              setTotalRows(responseData.totalRows);
              setLoading(false);
            } else {
              setExhibitions([]);
              setTotalRows(0);
              setPerPage(10);
              setCurrentPage(0);
              setLoading(false);
            }
          })
          .catch(error => {
            if (error.response.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error?.response?.data?.message || "Erro ao listar exposições",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [
      timeout,
      setLoading,
      setExhibitions,
      setTotalRows,
      setPerPage,
      setCurrentPage,
      signOut,
      navigate,
      toastr,
    ],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: "asc" | "desc",
  ) => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay: 0,
    });
  };

  useEffect(() => {
    const field = filterField;
    const value = filterValue;
    const precision = filterPrecision;

    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field,
      value,
      precision,
      delay,
    });
  }, [
    filterField,
    filterPrecision,
    filterValue,
    delay,
    currentPage,
    orderDirection,
    orderField,
    perPage,
    handleListAll,
  ]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const saveFiltersToStorage = useCallback(() => {
    const filters = {
      filterField,
      filterPrecision,
      filterValue,
      gridFilterAreaOpen,
      currentPage,
      perPage,
      orderField,
      orderDirection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    orderField,
    orderDirection,
  ]);

  useEffect(() => {
    saveFiltersToStorage();
  }, [
    filterField,
    filterPrecision,
    filterValue,
    gridFilterAreaOpen,
    currentPage,
    perPage,
    saveFiltersToStorage,
  ]);

  const handleClearFilters = () => {
    setFilterField("");
    setFilterPrecision("containing");
    setFilterValue("");
    setCurrentPage(0);
    setPerPage(10);
    setOrderField("date");
    setOrderDirection("desc");
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <TitleContainer>Exposições</TitleContainer>
      <PageCard>
        <FilterButtonGroup>
          <div>
            <Tooltip title="Filtros" placement="top">
              <IconButton
                onClick={() => setGridFilterAreaOpen(!gridFilterAreaOpen)}
                size="small"
                color="primary"
                style={{ alignItems: "flex-end" }}
              >
                {gridFilterAreaOpen ? (
                  <FilterAltOffOutlinedIcon />
                ) : (
                  <FilterAltOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>

            {gridFilterAreaOpen && (
              <Tooltip title="Limpar Filtros" placement="top">
                <IconButton
                  onClick={() => handleClearFilters()}
                  size="small"
                  color="primary"
                  style={{ alignItems: "flex-end" }}
                >
                  <CleaningServicesOutlined />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <Tooltip title="Criar usuário" placement="top">
            <IconButton
              onClick={() => handleToUser(null)}
              size="small"
              color="primary"
              style={{ alignItems: "flex-end" }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </FilterButtonGroup>

        {gridFilterAreaOpen && (
          <GridFilterArea container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter">Filtrar por</InputLabel>
                <Select
                  label="Filtrar Por"
                  value={`${filterField}`}
                  onChange={e => {
                    setFilterField(`${e.target.value}`);
                  }}
                >
                  {fields.map(filter => {
                    return (
                      <MenuItem key={filter.field} value={filter.field}>
                        {filter.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Precisão</InputLabel>
                <Select
                  label="Precisão"
                  value={`${filterPrecision}`}
                  onChange={e => {
                    setFilterPrecision(`${e.target.value}`);
                  }}
                >
                  <MenuItem key={"equal"} value={"equal"}>
                    Igual
                  </MenuItem>
                  <MenuItem key={"containing"} value={"containing"}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>
          </GridFilterArea>
        )}

        <TableContainer
          sx={{
            minHeight: "250px",
          }}
        >
          <Table
            sx={{
              padding: "16px",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "&:last-child td, &:last-child th": {
                    borderBottom: 1,
                    borderColor: "#E5E5E5",
                  },
                }}
              >
                <TableCell size="small" width={60}>
                  Ações
                </TableCell>

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="name"
                  label="Nome"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="date"
                  label="Data"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="finished"
                  label="Status"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />

                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="zipCode"
                  label="CEP"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="stateId"
                  label="UF"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="city"
                  label="Cidade"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="details"
                  label="Observação"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {exhibitions.length > 0 &&
                exhibitions.map(exhibition => {
                  return (
                    <TableRow
                      key={exhibition.id}
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                      }}
                    >
                      <CustomMenuItem
                        id={exhibition.id ? exhibition.id : ""}
                        handleToEdit={
                          user?.typeId === 1 ? handleToUser : undefined
                        }
                        requestNumber={exhibition.id}
                        handleToFollowUp={
                          user?.typeId === 1
                            ? () => handleToFollowUp(exhibition.id ?? null)
                            : undefined
                        }
                        handleToResults={
                          user?.typeId === 1
                            ? () => handleToResults(exhibition.id ?? null)
                            : undefined
                        }
                        handleToBestCollectors={
                          user?.typeId === 1
                            ? () =>
                                handleToBestCollectors(exhibition.id ?? null)
                            : undefined
                        }
                        handleToDownloadPlantsReport={
                          user?.typeId === 1 || user?.typeId === 4
                            ? () =>
                                handleOpenDownloadDialog(
                                  exhibition.id ? exhibition.id : "",
                                )
                            : undefined
                        }
                        handleToDownloadCollectorsReport={
                          user?.typeId === 1 || user?.typeId === 4
                            ? () =>
                                handleOpenDownloadCollectorsDialog(
                                  exhibition.id ? exhibition.id : "",
                                )
                            : undefined
                        }
                        handleToReception={
                          exhibition.allowRegistration &&
                          (user?.typeId === 1 || user?.typeId === 4)
                            ? () =>
                                navigate(
                                  `/received/exhibition/${exhibition.id}`,
                                )
                            : undefined
                        }
                        handleToResultsReport={
                          user?.typeId === 1
                            ? handleDownloadResultsReport
                            : undefined
                        }
                        handleToDuplicate={
                          user?.typeId === 1
                            ? () =>
                                handleDuplicateExhibition(
                                  exhibition.id ? exhibition.id : "",
                                )
                            : undefined
                        }
                        handleToGenerateJudgesCertificates={
                          user?.typeId === 1
                            ? () =>
                                handleGenerateJudgesCertificates(
                                  exhibition.id ? exhibition.id : "",
                                )
                            : undefined
                        }
                        finished={exhibition.finished}
                      />

                      <CustomTableCell
                        size="small"
                        title={exhibition.name}
                        width={300}
                      >
                        {exhibition.name}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={150}>
                        {exhibition.date
                          ? dayjs(exhibition.date).format("DD/MM/YYYY")
                          : ""}
                      </CustomTableCell>
                      <CustomTableCell size="small" width={135}>
                        {exhibition.finished ? "Finalizada" : "Em andamento"}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={100}>
                        {exhibition.zipCode}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={50}>
                        {exhibition.stateId}
                      </CustomTableCell>

                      <CustomTableCell size="small" width={100}>
                        {exhibition.city}
                      </CustomTableCell>

                      <CustomTableCell
                        size="small"
                        title={exhibition.details}
                        width={400}
                      >
                        {exhibition.details}
                      </CustomTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {exhibitions.length === 0 && (
            <CustomNoContentTableBody description="Nenhuma categoria encontrada" />
          )}
        </TableContainer>
        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />

        <PrintPdfDialog
          open={downloadDialogOpen}
          onClose={handleCloseDownloadDialog}
          exhibitionId={selectedExhibitionId ?? ""}
          allowSplit
          mode="download"
        />

        <PrintCollectorsPdfDialog
          open={downloadCollectorsDialogOpen}
          onClose={handleCloseDownloadCollectorsDialog}
          exhibitionId={selectedExhibitionId ?? ""}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
